import { useQuery } from "@apollo/client";
import { ODOO_COMPANY_IDS } from "AppSettings";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import Status from "components/base/Status";
import { useModals } from "ModalProvider";
import moment from "moment";
import { OdooContext } from "OdooProvider";
import { useContext, useState } from "react";
import { BsArrowRight } from "react-icons/bs";
import { GoHistory } from "react-icons/go";
import { IoMdHammer } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import { searchByProp } from "utils/search";
import CreatePlan from "./CreatePlan";
import OrderSchedulerDescription from "./docs/OrderSchedulerDescription";
import { FETCH_PREV_PLANS } from "./graphql";
import PlanLogsView from "./PlanLogsView";
import ReorderView from "./Reorderview";

const SALES_STATES = ["SALES_CONFIRMED", "AWAITING_CUSTOMER"];
const IN_QUEUE_STATES = ["AWAITING_PRODUCTION", "PLANNING"];
const PAGE_SIZE = 200;

const PlanList = ({ handled }) => {
  const [searchText, setSearchText] = useState("");

  let variables = {
    statesIn: [
      "DRAFT",
      "SALES_CONFIRMED",
      "AWAITING_CUSTOMER",
      "AWAITING_PRODUCTION",
      "PLANNING",
    ],
  };
  if (handled) {
    variables["statesIn"] = ["HANDLED", "PROCESSING"];
    variables["rootPlans"] = true;
    variables["options"] = { orderBy: "-handle_at", limit: PAGE_SIZE };
  }
  const { loading, error, data } = useQuery(FETCH_PREV_PLANS, {
    variables,
    fetchPolicy: "network-only",
  });
  const navigate = useNavigate();
  const modal = useModals();

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  let orderingNumber = 0;

  const draftPlans = data.allProductionPlans.results.filter(
    (i) => i.state === "DRAFT",
  );
  const salesPlans = data.allProductionPlans.results.filter((i) =>
    SALES_STATES.includes(i.state),
  );
  const currentPlans = data.allProductionPlans.results.filter((i) =>
    IN_QUEUE_STATES.includes(i.state),
  );
  const handledPlans = data.allProductionPlans.results.filter(
    (i) => i.state === "HANDLED",
  );
  const processingPlans = data.allProductionPlans.results.filter(
    (i) => i.state === "PROCESSING",
  );

  const plans = [
    ...handledPlans,
    ...processingPlans,
    ...currentPlans,
    ...salesPlans,
    ...draftPlans,
  ]
    .map((i) => ({
      ...i,
      orderingNumber: IN_QUEUE_STATES.includes(i.state)
        ? ++orderingNumber
        : null,
      searchString: `${i.name} ${i.wisCustomer?.name} ${i.odooObject?.objectName}`,
    }))
    .filter((i) => searchByProp(i, ["searchString"], searchText));

  function createPlan() {
    modal.present({
      title: "Create a Plan",
      center: true,
      maxWidth: "max-w-3xl",
      children: <CreatePlan onCreatedPlan={(plan) => navigate(plan.id)} />,
    });
  }

  function reorderPlans() {
    modal.present({
      title: "Sort Plans",
      center: false,
      maxWidth: "max-w-5xl",
      children: <ReorderView onCompleted={modal.hide} />,
    });
  }

  function showLogs(plan) {
    modal.present({
      title: "Compute Logs",
      subtitle: plan.name,
      maxWidth: "max-w-6xl",
      children: <PlanLogsView planId={plan.id} />,
    });
  }

  return (
    <div className="p-6 grid gap-6 grid-cols-12">
      <div className="col-span-full">
        <div className="card animate-fadeInDown">
          <div className="flex justify-between space-x-8">
            {handled ? null : (
              <Button onClick={createPlan}>+ Create Plan</Button>
            )}

            <div className="flex-1">
              <SearchBar
                value={searchText}
                onChange={setSearchText}
                className="bg-gray-100 dark:bg-gray-700 dark:bg-opacity-30"
                placeholder="Search by plan name, customer, or Odoo order / transfer name."
              />
            </div>

            {handled ? null : (
              <div className="flex space-x-6">
                <Button onClick={reorderPlans}>Sort Plans</Button>
              </div>
            )}
          </div>

          <div className="mt-2 -mx-2">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Customer</th>
                  <th>Production Date</th>
                  <th>Type</th>
                  <th>Odoo</th>
                  <th className="text-right">Created</th>
                  {handled && <th className="text-right">Handed</th>}
                  <th className="text-right pr-4">State</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan, index) => (
                  <PlanRowView
                    key={plan.id}
                    index={index}
                    plan={plan}
                    handled={handled}
                    showLogs={() => showLogs(plan)}
                  />
                ))}
              </tbody>
              {data.allProductionPlans.total > PAGE_SIZE && (
                <tfoot>
                  <tr>
                    <td colSpan="7">
                      <div className="opacity-70 text-xs">
                        Showing the latest handled and processing {PAGE_SIZE} of{" "}
                        {data.allProductionPlans.total} plans. To view all
                        plans, please go to{" "}
                        <a
                          href={`${process.env.REACT_APP_SERVER_ADMIN_URL}product/productionplan`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          admin
                        </a>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div
          className="animate-fadeInDown opacity-0"
          style={{ animationDelay: "0.5s" }}
        >
          <OrderSchedulerDescription />
        </div>
      </div>
    </div>
  );
};

const PlanRowView = ({ plan, index, handled, showLogs }) => {
  const { odooUrl } = useContext(OdooContext);

  return (
    <tr
      className="py-2 border-t border-gray-100 dark:border-gray-700 animate-fadeInDown opacity-0"
      style={{ animationDelay: `${Math.min(index * 0.035, 1)}s` }}
    >
      <td className="w-8">
        <div className="flex items-center space-x-2">
          {plan.orderingNumber ? (
            <span className="rounded-full bg-teal-600 text-white w-5 h-5 flex items-center justify-center text-xs scale-90">
              {plan.orderingNumber}
            </span>
          ) : plan.state === "HANDLED" ? (
            <div className="rounded-full bg-indigo-600 text-white w-5 h-5 flex items-center justify-center text-[9px]">
              <IoMdHammer className="text-white dark:text-gray-300" />
            </div>
          ) : null}
          <Link to={plan.id} className="whitespace-nowrap">
            {plan.name}
          </Link>
        </div>
      </td>
      <td>{plan.wisCustomer ? plan.wisCustomer.name : " - "}</td>
      <td>
        <div className="flex items-center space-x-2 mt-px whitespace-nowrap">
          <div>{moment(plan.startDate).format("YYYY-MM-DD")}</div>
          <BsArrowRight />
          <div>
            {plan.readyDate
              ? moment(plan.readyDate).format("YYYY-MM-DD")
              : "..."}
          </div>
          <Button onClick={showLogs}>
            <GoHistory />
          </Button>
        </div>
      </td>
      <td>{plan.planType}</td>
      <td className="whitespace-nowrap">
        {plan.odooObject ? (
          <a
            target="_blank"
            className="text-purple-600"
            rel="noreferrer"
            href={
              plan.odooObject.objectType === "TRANSFER"
                ? `${odooUrl}/web#id=${plan.odooObject.objectId}&action=350&active_id=17&model=stock.picking&view_type=form&menu_id=222`
                : `${odooUrl}/web#id=${plan.odooObject.objectId}&menu_id=408&cids=${
                    ODOO_COMPANY_IDS[plan.wisCustomer.system] || 1
                  }&action=312&model=sale.order&view_type=form`
            }
          >
            {plan.odooObject.objectName}
          </a>
        ) : (
          "-"
        )}
      </td>
      <td className="text-right">{plan.createdBy.firstName}</td>

      {handled && <td className="text-right">{plan.handledBy?.firstName}</td>}

      <td>
        <div className="flex justify-end items-center">
          {plan.nextActionUser && (
            <b className="text-orange-600">{plan.nextActionUser.firstName}</b>
          )}
          <Status status={plan.state} />
        </div>
      </td>
    </tr>
  );
};

export default PlanList;
